import { useLazyQuery } from '@apollo/client';
import { GetChangeRequestsByApprovalDocument } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

/**
 * Imperative (lazy) read of the PENDING change requests touching a given
 * approval config — RSP-6099. ApprovalForm calls this on demand before an
 * edit/delete and awaits a FRESH (network-only) result, so the declarative
 * `createQueryHook` factory does not fit. Both data paths live here so the
 * consumer never touches Apollo directly.
 *
 * - trpc ON  → `queryClient.fetchQuery` with `staleTime: 0` forces a fresh
 *   fetch (matching Apollo's `network-only`).
 * - trpc OFF → the legacy Apollo `useLazyQuery` with `fetchPolicy: 'network-only'`.
 */
export const useGetChangeRequestsByApproval = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [getInFlightChangeRequests] = useLazyQuery(
    GetChangeRequestsByApprovalDocument,
    {
      fetchPolicy: 'network-only',
    }
  );

  const getChangeRequestsByApproval = useCallback(
    async (approvalId: string): Promise<{ Id: string }[]> => {
      if (trpcEnabled) {
        const rows = await queryClient.fetchQuery({
          ...trpc.frontend.changeRequest.changeRequestsByApproval.queryOptions({
            approvalId,
          }),
          staleTime: 0,
        });

        return rows;
      }

      const result = await getInFlightChangeRequests({
        variables: { approvalId },
      });

      return result.data?.change_request ?? [];
    },
    [trpcEnabled, queryClient, trpc, getInFlightChangeRequests]
  );

  return { getChangeRequestsByApproval };
};
